import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

import { ParagraphNoSpace } from 'src/components/atoms/Typography'

import { Planting, Season } from 'src/config/generated/graphql'
import {
  applicationPlantingReportPath,
  editApplicationPath,
  viewApplicationPath
} from 'src/config/paths'
import { ApplicationWorkflowState } from 'src/types/application'
import { PlantingWorkflowState } from 'src/types/planting'

import { Attention, WorkflowStatus } from '../../WorkflowStatus'

dayjs.extend(isSameOrBefore)

const FULLY_MONITORED_PERCENT = 100
const MONITORING_YEARS = 3

type MonitoringStats = {
  [year: number]: number // monitoredQuantity per monitoring year (1, 2, 3)
}

/**
 * Calculates the current monitoring year based on which monitoring periods exist in plantings.
 * Returns:
 * - 1, 2, or 3 for the next monitoring year that should be added
 * - null if all 3 years of monitoring are complete
 */
export const calculateMonitoringYear = (
  plantings: Planting[]
): number | null => {
  // Collect all monitoring events with their monitored dates
  const allEvents: Array<{ monitoredOn: string }> = []
  plantings.forEach(p => {
    p.MonitoringEvents?.forEach(event => {
      if (event.MonitoredOn) {
        allEvents.push({ monitoredOn: event.MonitoredOn })
      }
    })
  })

  if (allEvents.length === 0) {
    return 1 // No monitoring yet, we're in year 1
  }

  // Sort events by monitored date
  allEvents.sort(
    (a, b) => dayjs(a.monitoredOn).valueOf() - dayjs(b.monitoredOn).valueOf()
  )

  // Count distinct monitoring periods
  let periodCount = 1
  const firstEvent = allEvents[0]
  if (!firstEvent) {
    return 1
  }

  let periodStartDate = dayjs(firstEvent.monitoredOn)

  for (let i = 1; i < allEvents.length; i += 1) {
    const currentEvent = allEvents[i]
    if (currentEvent) {
      const monthsDiff = dayjs(currentEvent.monitoredOn).diff(
        periodStartDate,
        'month'
      )

      // If events are monitored 9 months or more from the period start, consider them a different monitoring year
      if (monthsDiff >= 9) {
        periodCount += 1
        periodStartDate = dayjs(currentEvent.monitoredOn)
      }
    }
  }

  // If we have 3 or more periods, all monitoring is complete
  if (periodCount >= MONITORING_YEARS) {
    return null
  }

  // Return the current monitoring year we're in
  return periodCount
}

/**
 * Computes monitoring stats per year from plantings.
 * Assigns monitoring events to Year 1, 2, or 3 based on chronological order of monitoring dates,
 * not based on calendar deadlines.
 */
const getMonitoringStats = (
  plantings: Planting[]
): { stats: MonitoringStats; totalQuantity: number } => {
  const totalQuantity = plantings.reduce((sum, p) => sum + p.Quantity, 0)
  const stats: MonitoringStats = {}

  // Collect all monitoring events with their monitored dates and quantities
  const allEvents: Array<{ monitoredOn: string; quantity: number }> = []
  plantings.forEach(p => {
    const quantity = p.Quantity
    p.MonitoringEvents?.forEach(event => {
      if (event.MonitoredOn) {
        allEvents.push({ monitoredOn: event.MonitoredOn, quantity })
      }
    })
  })

  if (allEvents.length === 0) {
    return { stats, totalQuantity }
  }

  // Sort events by monitored date
  allEvents.sort(
    (a, b) => dayjs(a.monitoredOn).valueOf() - dayjs(b.monitoredOn).valueOf()
  )

  // Identify distinct monitoring periods by finding significant time gaps
  // Group events that are monitored within 9 months from the period start into the same monitoring year
  const monitoringPeriods: Array<
    Array<{ monitoredOn: string; quantity: number }>
  > = []
  const firstEvent = allEvents[0]
  if (!firstEvent) {
    return { stats, totalQuantity }
  }

  let currentPeriod: Array<{ monitoredOn: string; quantity: number }> = [
    firstEvent
  ]
  let periodStartDate = dayjs(firstEvent.monitoredOn)

  for (let i = 1; i < allEvents.length; i += 1) {
    const currentEvent = allEvents[i]

    if (currentEvent) {
      const monthsDiff = dayjs(currentEvent.monitoredOn).diff(
        periodStartDate,
        'month'
      )

      // If events are monitored 9 months or more from the period start, consider them a different monitoring year
      if (monthsDiff >= 9) {
        monitoringPeriods.push(currentPeriod)
        currentPeriod = [currentEvent]
        periodStartDate = dayjs(currentEvent.monitoredOn)
      } else {
        currentPeriod.push(currentEvent)
      }
    }
  }
  monitoringPeriods.push(currentPeriod)

  // Assign each period to Year 1, 2, or 3 and calculate stats
  monitoringPeriods.forEach((period, index) => {
    const year = index + 1
    if (year <= MONITORING_YEARS) {
      period.forEach(event => {
        stats[year] = (stats[year] ?? 0) + event.quantity
      })
    }
  })

  return { stats, totalQuantity }
}
/**
 * Returns percentage capped at 100%
 */
export const getCappedPercentage = (value: number, base: number): number => {
  if (!base) return 0

  const percent = (value * 100) / base
  return Math.min(Math.floor(percent), 100)
}

/**
 * Calculates percent of approved trees that have been planted (excluding Draft plantings)
 */
export const calculatePlantedTreesPercent = (
  approvedTrees: number,
  plantings: Planting[]
): number => {
  const totalPlanted = plantings
    .filter(p => p.WorkflowState !== PlantingWorkflowState.Draft)
    .reduce((sum, p) => sum + p.Quantity, 0)

  return getCappedPercentage(totalPlanted, approvedTrees)
}

/**
 * Calculates the percent of trees monitored for a given monitoring year
 */
const calculateMonitoredTreesPercentForYear = (
  plantings: Planting[],
  year: number
): number => {
  const { stats, totalQuantity } = getMonitoringStats(plantings)

  if (totalQuantity === 0) return 0

  const monitoredQuantity = stats[year] ?? 0
  return getCappedPercentage(monitoredQuantity, totalQuantity)
}

/**
 * Returns the latest monitoring year for which monitoring is fully complete (100%).
 * Returns 1 if no monitoring done
 */
const getLatestMonitoringYear = (plantings: Planting[]): number => {
  const { stats, totalQuantity } = getMonitoringStats(plantings)

  if (totalQuantity === 0) return 1

  for (let year = MONITORING_YEARS; year >= 1; year -= 1) {
    const monitoredQuantity = stats[year] ?? 0
    const percent = getCappedPercentage(monitoredQuantity, totalQuantity)
    if (percent >= FULLY_MONITORED_PERCENT) {
      return year
    }
  }

  return 1
}

/**
 * Renders the monitoring status message.
 * latestMonitoringYear is always 1, 2, or 3 (never 0 or null)
 */
const renderMonitoringStatus = (
  monitoredPercent: number,
  latestMonitoringYear: number
): string => {
  return `${monitoredPercent}% monitored for Year ${latestMonitoringYear}`
}

export const getStateName = (
  state: ApplicationWorkflowState,
  season: Season,
  approvedTrees: number,
  plantings: Planting[]
) => {
  const now = dayjs()
  const submissionDueDate = dayjs(season.SubmissionDueDate)
  const isSubmissionPastDue = now.isAfter(submissionDueDate)

  switch (state) {
    case ApplicationWorkflowState.Draft:
      return isSubmissionPastDue ? 'Draft (expired)' : 'Draft'

    case ApplicationWorkflowState.Planted:
      return `${calculatePlantedTreesPercent(
        approvedTrees,
        plantings
      )}% confirmed as planted`

    case ApplicationWorkflowState.Pending:
      return 'Submitted'

    case ApplicationWorkflowState.Monitoring: {
      if (!season.SubmissionDueDate || !season.MonitoringDueDate) return null
      const latestMonitoringYear = getLatestMonitoringYear(plantings)

      const monitoredPercent = calculateMonitoredTreesPercentForYear(
        plantings,
        latestMonitoringYear
      )

      return renderMonitoringStatus(monitoredPercent, latestMonitoringYear)
    }

    default:
      return state
  }
}

/**
 * Determines the level of attention (Low, Moderate, High) based on how close a due date is,
 * using different thresholds for moderate and high alerts.
 */
export const getAttentionLevel = (
  dueDate: string,
  moderateThreshold: number,
  moderateUnit: dayjs.ManipulateType,
  highThreshold: number,
  highUnit: dayjs.ManipulateType
): Attention => {
  const now = dayjs()
  const due = dayjs(dueDate)

  const highDiff = due.diff(now, highUnit)
  if (highDiff <= highThreshold) {
    return Attention.High
  }

  const moderateDiff = due.diff(now, moderateUnit)
  if (moderateDiff <= moderateThreshold) {
    return Attention.Moderate
  }

  return Attention.Low
}

/*
 Example:
 latestMonitoringYear	monitoringDueDate	Output
 1	                  2024-07-01	      2024-07-01
 2	                  2024-07-01	      2025-07-01
 3	                  2024-07-01	      2026-07-01
 */
const calcMonitoringDueDate = (
  latestMonitoringYear: number,
  monitoringDueDate: string
): string =>
  dayjs(monitoringDueDate)
    .add(latestMonitoringYear - 1, 'year')
    .toISOString()

export const getNextStep = (
  state: ApplicationWorkflowState,
  season: Season,
  approvedTrees: number,
  plantings: Planting[]
): React.ReactElement | null => {
  if (!season.SubmissionDueDate || !season.MonitoringDueDate) return null

  const now = dayjs()
  const submissionDueDate = dayjs(season.SubmissionDueDate)
  const isSubmissionPastDue = now.isAfter(submissionDueDate)
  const currentMonitoringYear = calculateMonitoringYear(plantings)
  const latestMonitoringYear = getLatestMonitoringYear(plantings)

  switch (state) {
    case ApplicationWorkflowState.Draft:
      return season.SubmissionDueDate && !isSubmissionPastDue ? (
        <>
          <ParagraphNoSpace>Submit Tree Request</ParagraphNoSpace>
          <WorkflowStatus
            attention={getAttentionLevel(
              season.SubmissionDueDate,
              2,
              'month',
              2,
              'week'
            )}
            date={dayjs(season.SubmissionDueDate).toDate()}
          />
        </>
      ) : null

    case ApplicationWorkflowState.Pending:
    case ApplicationWorkflowState.Declined:
    case ApplicationWorkflowState.Withdrawn:
      return null

    case ApplicationWorkflowState.Approved:
      return (
        <>
          <ParagraphNoSpace>Confirm planting</ParagraphNoSpace>
          {season.PlantingDueDate && (
            <WorkflowStatus
              attention={getAttentionLevel(
                season.PlantingDueDate,
                3,
                'month',
                2,
                'month'
              )}
              date={dayjs(season.PlantingDueDate).toDate()}
            />
          )}
        </>
      )
    case ApplicationWorkflowState.Planted: {
      const isPlantingComplete =
        calculatePlantedTreesPercent(approvedTrees, plantings) >=
        FULLY_MONITORED_PERCENT
      if (season.PlantingDueDate && !isPlantingComplete) {
        return (
          <>
            <ParagraphNoSpace>Update planting</ParagraphNoSpace>
            <WorkflowStatus
              attention={getAttentionLevel(
                season.PlantingDueDate,
                3,
                'month',
                2,
                'month'
              )}
              date={dayjs(season.PlantingDueDate).toDate()}
            />
          </>
        )
      }

      if (isPlantingComplete) {
        const monitoringDueDate = calcMonitoringDueDate(
          latestMonitoringYear,
          season.MonitoringDueDate
        )
        return (
          <>
            <ParagraphNoSpace>
              {currentMonitoringYear
                ? `Add Year ${latestMonitoringYear} monitoring`
                : 'Add monitoring'}
            </ParagraphNoSpace>
            {!!currentMonitoringYear && (
              <WorkflowStatus
                attention={getAttentionLevel(
                  monitoringDueDate,
                  3,
                  'month',
                  2,
                  'month'
                )}
                date={dayjs(monitoringDueDate).toDate()}
              />
            )}
          </>
        )
      }
      return null
    }
    case ApplicationWorkflowState.Monitoring: {
      const monitoredPercent = calculateMonitoredTreesPercentForYear(
        plantings,
        latestMonitoringYear
      )

      if (
        monitoredPercent === FULLY_MONITORED_PERCENT &&
        latestMonitoringYear < MONITORING_YEARS
      ) {
        const nextMonitoringYear = latestMonitoringYear + 1
        const monitoringDueDate = calcMonitoringDueDate(
          nextMonitoringYear,
          season.MonitoringDueDate
        )
        return (
          <>
            <ParagraphNoSpace>{`Add Year ${nextMonitoringYear} monitoring`}</ParagraphNoSpace>
            <WorkflowStatus
              attention={getAttentionLevel(
                monitoringDueDate,
                3,
                'month',
                2,
                'month'
              )}
              date={dayjs(monitoringDueDate).toDate()}
            />
          </>
        )
      }
      const monitoringDueDate = calcMonitoringDueDate(
        latestMonitoringYear,
        season.MonitoringDueDate
      )
      return monitoredPercent < FULLY_MONITORED_PERCENT &&
        !!currentMonitoringYear ? (
        <>
          <ParagraphNoSpace>{`Add Year ${latestMonitoringYear} monitoring`}</ParagraphNoSpace>
          <WorkflowStatus
            attention={getAttentionLevel(
              monitoringDueDate,
              3,
              'month',
              2,
              'month'
            )}
            date={dayjs(monitoringDueDate).toDate()}
          />
        </>
      ) : (
        <ParagraphNoSpace>Update monitoring</ParagraphNoSpace>
      )
    }

    default:
      return <ParagraphNoSpace>Complete!</ParagraphNoSpace>
  }
}

export const getPath = (
  applicationId: string,
  projectId: string,
  state: ApplicationWorkflowState,
  season: Season
) => {
  const now = dayjs()
  const submissionDueDate = dayjs(season.SubmissionDueDate)
  const isSubmissionPastDue = now.isAfter(submissionDueDate)
  switch (state) {
    case ApplicationWorkflowState.Draft:
      if (isSubmissionPastDue) {
        return undefined
      }
      return editApplicationPath({ routeParams: { applicationId, projectId } })
    case ApplicationWorkflowState.Pending:
    case ApplicationWorkflowState.Declined:
    case ApplicationWorkflowState.Withdrawn:
      return undefined
    case ApplicationWorkflowState.Approved:
      return applicationPlantingReportPath({
        routeParams: {
          applicationId,
          projectId
        }
      })
    case ApplicationWorkflowState.Planted:
    case ApplicationWorkflowState.Monitoring:
      return applicationPlantingReportPath({
        routeParams: {
          projectId,
          applicationId
        }
      })
    default:
      return viewApplicationPath({
        routeParams: { applicationId, projectId }
      })
  }
}
